import React from "react";

import { CheckCircle2, Circle, Clock } from "lucide-react";

import { bqCountText, bqSummary } from "./briefingAnswers";
import { FLOW } from "./flowCopy";
import { planFlow } from "./planFlow";
import type { AccountPlanOverview, PlanFile } from "../../Types/AccountPlan";
import { STALLED_LINE } from "../../Utils/AccountPlans/queueText";

/** "stalled": our failure, read as taking longer than usual (P8); never red. */
type PartState = "ready" | "pending" | "stalled" | "waiting";

export interface BriefingPart {
  key: "briefing" | "questions" | "chat";
  label: string;
  state: PartState;
  detail: string;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/**
 * Who Ask about this plan answers from, for the version under review. Since
 * 2026-11-01 (product review) chat answers only from a PUBLISHED version, for the
 * team and the sponsor alike: before the first publish it waits; while a newer
 * draft is in review it stays on the published one.
 */
export const askAboutPlanLine = (
  data: AccountPlanOverview,
  file: PlanFile,
  /** The viewer publishes (the AE); anyone else waits on the AE (UI tour item 11). */
  publishes = true
): { live: boolean; text: string } => {
  // The plan number only (D1); `version` is internal and never shown.
  const vN =
    file.plan_version != null ? `v${file.plan_version}` : "this version";
  const published = planFlow(data).published;
  if (published && published.fileId === file.id)
    return {
      live: true,
      text: `Your team and the sponsor can ask about ${vN}.`,
    };
  if (published)
    return {
      live: true,
      text: publishes
        ? `Answers from v${published.planVersion} until you publish ${vN}.`
        : `Answers from v${published.planVersion} until the AE publishes ${vN}.`,
    };
  return {
    live: false,
    text: publishes
      ? `Opens when you publish ${vN}, for your team and the sponsor.`
      : `Opens when the AE publishes ${vN}.`,
  };
};

/**
 * The three things one briefing run produces, with the same status each (Sushant,
 * 2026-10-31): the briefing, its Briefing Questions answers, and Ask about this plan.
 */
export const briefingStatusParts = (
  data: AccountPlanOverview,
  file: PlanFile,
  publishes = true
): BriefingPart[] => {
  const ready = file.sponsor_view_status === "ready";
  // P8 (Sushant, 2026-11-03): no "Briefing failed · Try again"; a briefing that can't be written retries, then reads as stalled.
  const stalled = file.sponsor_view_status === "failed";
  const sections = file.progress?.briefing?.steps?.write?.items?.length ?? 0;
  // One per-question state for every summary (UI tour item 5).
  const bq = bqSummary(file);
  const ask = askAboutPlanLine(data, file, publishes);
  return [
    {
      key: "briefing",
      label: FLOW.briefingPart,
      state: ready ? "ready" : "pending",
      detail: ready
        ? sections
          ? `${plural(sections, "section")} written and checked against the slides`
          : "Written and checked against the slides"
        : stalled
          ? STALLED_LINE
          : "Being written",
    },
    {
      key: "questions",
      label: FLOW.briefingQuestions,
      state:
        bq.status === "stalled"
          ? "stalled"
          : bq.status === "answered"
            ? "ready"
            : ready
              ? "waiting"
              : "pending",
      // P8 / item 20: a questions pass that failed reads as stalled, with no retry.
      detail:
        bq.status === "stalled"
          ? STALLED_LINE
          : bq.status === "answered"
            ? bq.partial || bq.missing
              ? bqCountText(bq).replace(/^[^·]*· /, "")
              : `${plural(bq.total, "question")} answered from the plan`
            : ready
              ? "Not answered for this briefing"
              : "Being answered",
    },
    {
      key: "chat",
      label: FLOW.askAboutPlan,
      state: ready ? (ask.live ? "ready" : "waiting") : "pending",
      detail: ready ? ask.text : "Opens once the briefing is published",
    },
  ];
};

interface Props {
  data: AccountPlanOverview;
  file: PlanFile;
  /** The viewer is the AE, who publishes. */
  publishes?: boolean;
}

/** Review briefing: what this run produced, side by side, once the briefing is ready. */
const BriefingStatus: React.FC<Props> = ({ data, file, publishes = true }) => (
  <ul
    data-testid="briefing-parts"
    aria-label="What this briefing includes"
    className="grid gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm md:grid-cols-3"
  >
    {briefingStatusParts(data, file, publishes).map(p => (
      <li
        key={p.key}
        data-testid={`briefing-part-${p.key}`}
        data-state={p.state}
        className="flex min-w-0 items-start gap-2"
      >
        {p.state === "ready" ? (
          <CheckCircle2
            className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
            aria-label="ready"
          />
        ) : p.state === "waiting" ? (
          <Clock
            className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
            aria-label="waiting"
          />
        ) : p.state === "stalled" ? (
          <Clock
            className="mt-0.5 h-4 w-4 shrink-0 text-gray-500"
            aria-label="taking longer than usual"
          />
        ) : (
          <Circle
            className="mt-0.5 h-4 w-4 shrink-0 text-gray-300"
            aria-label="pending"
          />
        )}
        <div className="min-w-0">
          <div className="font-medium text-gray-900">{p.label}</div>
          <div className="text-xs text-gray-600">{p.detail}</div>
        </div>
      </li>
    ))}
  </ul>
);

export default BriefingStatus;
